import {
    ChangeDetectionStrategy,
    Component,
    HostListener,
    Input,
    WritableSignal,
    signal,
} from "@angular/core";

import { Observable } from "rxjs";

import { InteractionType, MOUSE, TOUCH } from "@hermes/aphrodite/carousel";
import { IconButtonSize, MEDIUM, SMALL } from "@hermes/aphrodite/icon-button";
import { LayoutFacade } from "@hermes/aphrodite/layout";
import { Appareance, BORDER_RADIUS, SVG_ONLY } from "@hermes/aphrodite/tag";
import { ControlPosition, LOW_POSITION } from "@hermes/aphrodite/video";
import {
    PRODUCT_IN_GRID_MOBILE,
    PRODUCT_IN_GRID_DESKTOP,
    HERO_PRODUCT_IN_GRID_DESKTOP,
} from "@hermes/fragments/product-utils";
import {
    GridAsset,
    GridFacade,
    GridProduct,
} from "@hermes/states/grid-filters-state";
import { CarouselClickEvent } from "@hermes/states/product-page";
import { AnalyticsService } from "@hermes/utils/analytics";
import {
    AssetMediaType,
    SCENE7_IMAGE_BACKGROUND_COLOR,
} from "@hermes/utils-generic/constants";

/**
 * This component allows you to manage the display of alternative views on an item in the grid using the carousel component
 */
@Component({
    selector: "h-grid-result-alternative-views",
    templateUrl: "./grid-result-alternative-views.component.html",
    styleUrls: ["./grid-result-alternative-views.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridResultAlternativeViewsComponent {
    /** Current product */
    @Input()
    public product!: GridProduct;

    @Input()
    public isMobile: boolean = false;

    @Input()
    public shouldDisplayVtoTag: boolean = false;

    @Input()
    public shouldDisplayEngravingTag: boolean = false;

    @Input()
    public shouldDisplayVideo: boolean = false;

    @Input()
    public shouldDisplayTagDisplayOnly: ControlPosition = LOW_POSITION;

    @Input()
    public isFocused: boolean = false;

    @Input()
    public isFetchpriorityImage: boolean = false;

    public assetMediaType = AssetMediaType;

    public mouseOver: boolean = false;

    // Keeps track of the current index of the carousel slide.
    public carrousselCurrentIndex = 1;

    // Indicates whether the video is in a playing in swiper
    public isPlayVideo: boolean = true;

    public displayMode$: Observable<string> = this.gridFacade.displayMode$;

    public slideTo: WritableSignal<number> = signal(-1);

    public smallSize: IconButtonSize = SMALL;

    public mediumSize: IconButtonSize = MEDIUM;

    public borderRadius: Appareance = BORDER_RADIUS;

    public svgOnly: Appareance = SVG_ONLY;

    public lastInteractionType: InteractionType = MOUSE;

    constructor(
        public layoutFacade: LayoutFacade,
        private analyticsService: AnalyticsService,
        private gridFacade: GridFacade,
    ) {}

    private get videoIndex(): number {
        return this.product.assets.findIndex(
            (asset: GridAsset) => asset.type === AssetMediaType.Video,
        );
    }

    /*
    / A11y
    / Use Angular's HostListener to differentiate between touch and mouse interactions (mobile & desktop with zoom +200%).
    /
    */
    @HostListener("touchstart")
    public onTouched(): void {
        this.lastInteractionType = TOUCH;
    }

    /**
     * update currentInex carrousel & send analytics event when clicking next or prev
     */
    public updateCarouselIndex(currentIndex: number) {
        this.carrousselCurrentIndex = currentIndex;
        this.sendAnalyticsTagForCarouselChangeImage();
    }

    /**
     * Updates the playback state of the video in swiper.
     * @param isPlay
     */
    public onPlayPauseVideo(isPlay: boolean) {
        this.isPlayVideo = isPlay;
    }

    /** Manage the sending of events analytics when changing image */
    public sendAnalyticsTagForCarouselChangeImage(): void {
        this.analyticsService.sendData(
            new CarouselClickEvent({
                imageType: "grid",
                eventlabel: this.product.sku,
                sku: this.product.sku,
            }),
        );
    }

    public getImageSrc(imageUrl?: string, imageType?: string): string {
        if (!imageUrl) {
            return "";
        }
        switch (imageType) {
            case "grid": {
                return this.sizedImage(
                    this.isMobile
                        ? PRODUCT_IN_GRID_MOBILE
                        : PRODUCT_IN_GRID_DESKTOP,
                    imageUrl,
                );
            }
            case "hero": {
                if (this.isMobile === true) {
                    return this.sizedImage(PRODUCT_IN_GRID_MOBILE, imageUrl);
                }
                return this.sizedImage(HERO_PRODUCT_IN_GRID_DESKTOP, imageUrl);
            }
            default: {
                return imageUrl;
            }
        }
    }

    /**
     * Resize image
     *
     * @param size : height and width of scene7 image
     * @param url : url of image
     */
    public sizedImage(size: number, url: string): string {
        return `${url}&${SCENE7_IMAGE_BACKGROUND_COLOR}&wid=${size}&hei=${size}`;
    }

    public mouseleaveAlternativesViewsItemEvent(): void {
        if (this.videoIndex !== -1 && this.shouldDisplayVideo === true) {
            this.slideTo.set(this.videoIndex);
            this.carrousselCurrentIndex = this.videoIndex;
        }
    }

    public mouseenterAlternativesViewsItemEvent(): void {
        this.slideTo.set(-1);
    }

    public handleHover(): void {
        if (this.lastInteractionType === MOUSE) {
            this.mouseOver = true;
        }
    }
}
